import { BLACK_ONLY_EQUIPMENT_KIND, EQUIPMENTS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Equipment } from "../../../../../types/equipments";
import { AddressDisplay } from "../../../../../components/ui/addressDisplay/addressDisplay.component";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import PrintIcon from "@mui/icons-material/Print";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { Inventory2Outlined, Person4 } from "@mui/icons-material";
import { useHasPermission, useTenantName } from "../../../../../contexts/auth";
import EntityReferenceLink from "../../../../../components/ui/entityReference/entityReferenceLink.component";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../../../components/ui/detailView";
import { EquipmentConfigurationChips } from "../../../../../components/ui/equipmentConfigurationChips/equipmentConfigurationChips.component";
import { StatusChip } from "../../../../../components/ui/statusChip/statusChip.component";
import { formatDateTimeInline } from "../../../../../utils";

type EquipmentBasicInfoProps = {
  previewId?: string | number;
};

export const EquipmentBasicInfo = ({ previewId }: EquipmentBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  // A machine with no installation address is not out at a client's site - it
  // is back with us, so the workspace's own name is where it currently is.
  const tenantName = useTenantName();
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchEquipment = async () => {
      if (entityId) {
        const response = await get(entityId);
        setOverride(`/equipment-section/equipments/${entityId}`, response.data ? response.data.full_name : "");
        setEquipment(response.data);
      }
    };

    const handleEquipmentUpdated = () => {
      fetchEquipment();
    };

    window.addEventListener("equipmentUpdated", handleEquipmentUpdated);
    fetchEquipment();
    return () => {
      window.removeEventListener("equipmentUpdated", handleEquipmentUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!equipment) return <DetailPage loading />;
  // Black-only equipment has no color page cost - see BLACK_ONLY_EQUIPMENT_KIND.
  const isBlackOnly = equipment.kind === BLACK_ONLY_EQUIPMENT_KIND;
  const installationAddress = equipment.installation_address;

  return (
    <DetailPage>
      <DetailHeader
        icon={<PrintOutlinedIcon />}
        title={equipment.full_name}
        subtitle={equipment.serial && `${t("Serial Number")}: ${equipment.serial}`}
        chips={
          <>
            <StatusChip icon={<PrintIcon />} label={equipment.kind_display} color="primary" />
            <StatusChip icon={<Person4 />} label={equipment.ownership_display} color="secondary" />
            {equipment.is_archived && (
              <StatusChip
                icon={<Inventory2Outlined />}
                label={t("Archived")}
                title={t("Archived equipment does not count towards the subscription plan's device limit.")}
              />
            )}
          </>
        }
      />

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          <DetailRow label={t("Client Name")}>
            <EntityReferenceLink
              entityType="client"
              entityId={equipment.client_id}
              label={equipment.client_name}
              canPreview={canViewClients}
              stopPropagation={false}
            />
          </DetailRow>
          <DetailRow label={t("Producer")} value={equipment.producer} />
          <DetailRow label={t("Model")} value={equipment.model} />
          <DetailRow label={t("Serial Number")} value={equipment.serial} />
          <DetailRow label={t("Kind")} value={equipment.kind_display} />
          <DetailRow label={t("Ownership")} value={equipment.ownership_display} />
          <DetailRow label={t("Is installed")} value={equipment.is_installed ? t("Yes") : t("No")} />
          {equipment.is_archived && (
            <DetailRow label={t("Archived at")} value={formatDateTimeInline(equipment.archived_at)} />
          )}
          {/* Only shown once the machine has actually been sold - on rented
              equipment there is no guarantee of ours to report. */}
          {equipment.sold_at && (
            <>
              <DetailRow label={t("Sold at")} value={equipment.sold_at} />
              <DetailRow label={t("Warranty until")}>
                {equipment.warranty_until ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DetailValue>{equipment.warranty_until}</DetailValue>
                    <StatusChip
                      color={equipment.is_under_warranty ? "success" : "default"}
                      variant="outlined"
                      label={
                        equipment.is_under_warranty ? t("Under warranty") : t("Warranty expired")
                      }
                    />
                  </Box>
                ) : null}
              </DetailRow>
            </>
          )}
          {equipment.notes && <DetailRow label={t("Notes")} value={equipment.notes} multiline />}
        </DetailSection>

        <DetailSection title={t("Configuration")} padded>
          <EquipmentConfigurationChips attributeValues={equipment.attribute_values} />
        </DetailSection>

        {canViewProfitability && (
          <DetailSection title={t("Page costs")}>
            <DetailRow label={t("Black page cost")} value={equipment.bp_usage_cost} />
            {!isBlackOnly && <DetailRow label={t("Color page cost")} value={equipment.cp_usage_cost} />}
            <DetailRow label={t("Scanned page cost")} value={equipment.sp_usage_cost} />
          </DetailSection>
        )}

        <AddressDisplay
          address={installationAddress}
          title={`${t("Installation Address")} & ${t("Opening Hours")}`}
          withDetails
          withContact
          collapsible
          emptyContent={
            <Typography variant="subtitle1" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              {tenantName || "—"}
            </Typography>
          }
        />

      </DetailGrid>

      <DetailMetadata createdAt={equipment.created_at} updatedAt={equipment.updated_at} />
    </DetailPage>
  );
};
